// ── exports-helpers: the export≤view chooser model + the CSV download action ─
// Wave 10 (Exports). CONTRACT TRUTH (apps/vjal-coaching-api/src): GET
// /exports/:export_type returns text/csv for NINE types; each has a per-type VIEW
// permission; event-history is staff-only; reports CSV is D-3 visibility-
// filtered; transcripts are metadata-only. The backend does ONLY CSV — there is
// NO Excel/Word/PDF/PPT export. So this surface offers the real CSV per scoped
// type and renders the other formats as an honest "not available (CSV only)".
//
// ── RELEASE GATE: EXPORT ≤ VIEW (FR-PERM-002), scope frozen at request ───────
// The export type list a role sees + can download is `exportTypesForRole(role)`
// (the fail-closed allow-list in domain/logs-and-exports), which never exceeds a
// role's view scope. The scope is FROZEN at request: the chosen type is resolved
// against the role's allow-list at download time, so a stale/forged selection
// outside scope is refused before any request fires.

import { downloadBlob, humanizeLabel } from "@vjal/ui";

import type { CoachingRole } from "../../domain/roles";
import {
  COACHING_EXPORT_TYPES,
  NON_CSV_EXPORT_FORMATS,
  NON_CSV_FORMAT_LABEL,
  canExportType,
  exportTypesForRole,
  type CoachingExportType,
  type NonCsvExportFormat
} from "../../domain/logs-and-exports";

export {
  COACHING_EXPORT_TYPES,
  NON_CSV_EXPORT_FORMATS,
  NON_CSV_FORMAT_LABEL,
  exportTypesForRole,
  canExportType,
  type CoachingExportType,
  type NonCsvExportFormat
};

/** Human label for an export type (humanizeLabel; never a raw enum). */
export function exportTypeLabel(type: CoachingExportType): string {
  return type === "event-history" ? "Event history" : humanizeLabel(type);
}

/** A one-line note on what a given export type contains (honest about scope). */
export function exportTypeNote(type: CoachingExportType): string {
  switch (type) {
    case "reports":
      return "Report metadata only, visibility-filtered to your role (no confidential tiers, no report body).";
    case "transcripts":
      return "Transcript availability metadata only — the raw transcript text is never exported.";
    case "event-history":
      return "Append-only audit events (staff-only). Sensitive values are redacted.";
    default:
      return "Records you can view, scoped to your role at the time of export.";
  }
}

export interface ExportActionResult {
  ok: boolean;
  /** Set when the selection was outside the role's scope (export>view refused). */
  outOfScope?: boolean;
  /** EXPORT_FAILED posture marker for a failed download. */
  failed?: boolean;
  reference?: string;
}

export interface ExportDownloader {
  exportCsv: (exportType: string) => Promise<string>;
}

/**
 * Run a scoped CSV export → trigger a client-side download. The export≤view gate
 * is RE-CHECKED here (scope frozen at request): a type the role may not export is
 * refused BEFORE any request fires (`outOfScope`). A backend/transport failure
 * surfaces the EXPORT_FAILED posture (`failed`) — never a fabricated file.
 */
export async function runCsvExport(
  services: ExportDownloader,
  role: CoachingRole,
  type: CoachingExportType
): Promise<ExportActionResult> {
  // Freeze scope at request: refuse an out-of-scope type with NO network call.
  if (!canExportType(role, type)) {
    return { ok: false, outOfScope: true };
  }
  let csv: string;
  try {
    csv = await services.exportCsv(type);
  } catch (error) {
    // A transport/permission failure on the fetch = EXPORT_FAILED (never a file).
    return { ok: false, failed: true, reference: referenceOf(error) };
  }
  if (typeof csv !== "string" || csv.length === 0) {
    return { ok: false, failed: true };
  }
  // The download TRIGGER is a best-effort DOM side-effect: the CSV was fetched
  // successfully, so a missing createObjectURL (e.g. jsdom) must NOT be reported
  // as an export failure. downloadBlob already no-ops when document/URL is absent.
  try {
    downloadBlob(csv, exportFilename(type), "text/csv;charset=utf-8");
  } catch {
    // ignore: the export succeeded; only the browser download trigger was a no-op.
  }
  return { ok: true };
}

export function exportFilename(type: CoachingExportType): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `coaching-${type}-${stamp}.csv`;
}

function referenceOf(error: unknown): string | undefined {
  if (error && typeof error === "object" && "reference" in error) {
    const ref = (error as { reference?: unknown }).reference;
    if (typeof ref === "string" && ref.trim()) return ref;
  }
  return undefined;
}
